import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  dashboardService,
  type ResearchScheduleOverview,
  type ResearchScheduleRun,
  type ResearchScheduleUpdate,
} from '../../../services/dashboard.service';

const REFRESH_MS = 60_000;

const messageOf = (err: unknown, fallback: string) =>
  err instanceof Error && err.message ? err.message : fallback;

/**
 * The scheduled-research panel's data. Loads when the panel opens and
 * refreshes every minute while it stays open, so a run finishing overnight
 * (or the counter moving) shows without reopening it.
 */
export const useResearchSchedule = (isOpen: boolean) => {
  const [overview, setOverview] = useState<ResearchScheduleOverview | null>(null);
  const [runs, setRuns] = useState<ResearchScheduleRun[]>([]);
  const [targetAskRanges, setTargetAskRanges] = useState<{ label: string; min: number; max: number | null }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const load = () =>
      Promise.all([
        dashboardService.getResearchSchedule(),
        dashboardService.getResearchScheduleRuns(),
        dashboardService.getBucketedConstellation({ bucket_by: 'target_ask' }),
      ])
        .then(([o, r, t]) => {
          if (cancelled) return;
          setOverview(o.data);
          setRuns(r.data || []);
          setTargetAskRanges(t.data.bucket_ranges || []);
          setError(null);
        })
        .catch((err) => {
          if (!cancelled) setError(messageOf(err, 'Could not load the research schedule.'));
        });
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isOpen]);

  const update = useCallback(async (patch: ResearchScheduleUpdate, done?: string) => {
    setSaving(true);
    try {
      const res = await dashboardService.updateResearchSchedule(patch);
      setOverview(res.data);
      if (done) toast.success(done);
      return true;
    } catch (err) {
      // The backend's `detail` explains a rejected value — show it as-is.
      toast.error(messageOf(err, 'Could not save the research schedule.'));
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  // Loading until the first answer (or error) arrives after opening.
  const loading = isOpen && overview === null && error === null;

  return { overview, runs, targetAskRanges, loading, saving, error, update };
};
